import { CHAT_TYPE, ROUTE_NAMES } from "@root/constants";

export const isValidArray = (arr: TObject) =>
  arr && Array.isArray(arr) && arr.length;

export const isValidId = (id: TObject) => id && Number(id) > 0;

/**
 * Updates the `isExpended` property of the question at the specified index.
 * Sets the `isExpended` property to the provided value for the specified index,
 * and sets it to `false` for all other questions.
 *
 * @param {IFaqs[]} questions - The array of question objects.
 * @param {number} index - The index of the question to update.
 * @param {boolean} value - The boolean value to set for the `isExpended` property.
 * @returns {IFaqs[]} The updated array of question objects with the `isExpended` property updated.
 */
export const updateisExpended = (
  questions: IFaqs[],
  index: number,
  value: boolean
): IFaqs[] => {
  return questions.map((question, i) => ({
    ...question,
    isExpended: i === index ? value : false,
  }));
};

export const getLast4Digits = (cardNumber: string): string => {
  return cardNumber?.slice(-4);
};

/**
 * Adds a PDF ID to each item in the chat history array.
 * @param chatHistory - The original chat history array.
 * @param pdfId - The PDF ID to be added.
 * @returns A new chat history array with the PDF ID added to each item.
 */
export const addPdfIdToChatHistory = (
  chatHistory: any[],
  pdfId: string
): any[] => {
  return chatHistory.map((chat) => ({
    ...chat,
    pdf_id: pdfId,
  }));
};

/**
 * Filters chat history records by a specific PDF ID.
 * @param chatHistory - The chat history array.
 * @param pdfId - The PDF ID to filter by.
 * @returns A new array containing only the records with the specified PDF ID.
 */
export const filterChatHistoryByPdfId = (
  chatHistory: any[],
  pdfId: string
): any[] => {
  // Step 1: Filter the chat history based on the pdfId
  const filteredHistory = chatHistory.filter((chat) => chat?.pdf_id === pdfId);

  // Step 2: Remove pdf_id from each element in the filtered array
  const result = filteredHistory.map(({ pdf_id, ...rest }) => rest);

  return result;
};

export const carouselSliderProps = {
  superLargeDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 4000, min: 3000 },
    items: 5,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 3,
  },
};

export function getYouTubeVideoId(url: string): string | null {
  if (url) {
    const regex =
      /(?:youtube\.com\/(?:.*v=|v\/|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

    const match = url.match(regex);
    return match ? match[1] : null;
  }
  return null;
}

export function getDomainFromUrl(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname;
  } catch (error) {
    console.error("Invalid URL:", error);
    return null;
  }
}

export const getCustomFileName = (fileName: string) => {
  const fileExtension = fileName.split(".").pop() || "";
  const customFileName = `kte-${Date.now()}${
    fileExtension ? "." + fileExtension : ""
  }`;
  return customFileName;
};

export const getChatRoute = (chat: any) => {
  if (chat?.type === CHAT_TYPE.KCS) {
    return `/kcs-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.PROJECT_CHATBOT) {
    return `/document-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.MULTI_DOCUMENTS_QUERY) {
    return `/checklist-chat/${chat.id}`;
  } else if (chat?.type === CHAT_TYPE.DRAWING_ANALYSIS_CHAT) {
    return `/drawing-analysis-chat/${chat.id}`;
  } else {
    return `/chat/${chat.id}`;
  }
};

export const getChatType = (pathname: string) => {
  if (pathname?.includes(ROUTE_NAMES.KCS)) {
    return 6;
  } else if (pathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
    return 5;
  } else if (pathname?.includes(ROUTE_NAMES.DOCUMENT)) {
    return 2;
  } else if (pathname?.includes(ROUTE_NAMES.CHECKLIST)) {
    return 3;
  } else {
    return 1;
  }
};

export const getStatus = (status: any) => {
  if (status === 0) return "NOT_STARTED";
  if (status === 8) return "COMPLETED";
  if (status === 9) return "FAILED";
  return "PROCESSING";
};

export type ChunkInfo = {
  chunkNumber: number;
  totalChunks: number;
  chunk: Blob;
  fileName: string;
  fileSize: number;
};

export const splitFileIntoChunks = (
  file: File,
  chunkSize: number = 5 * 1024 * 1024 // 5MB
): ChunkInfo[] => {
  const chunks: ChunkInfo[] = [];
  const totalChunks = Math.ceil(file.size / chunkSize);

  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);

    chunks.push({
      chunkNumber: i + 1,
      totalChunks,
      chunk,
      fileName: file.name,
      fileSize: file.size,
    });
  }

  return chunks;
};

export const createChunkFormData = (
  chunkInfo: ChunkInfo,
  params: { mimeType: string; uploadId?: string; fileKey?: string }
) => {
  const formData = new FormData();
  formData.append("chunk", chunkInfo.chunk);
  formData.append("fileName", chunkInfo.fileName);
  formData.append("mimeType", params.mimeType || "application/octet-stream");
  formData.append("totalChunks", String(chunkInfo.totalChunks));
  formData.append("currentChunk", String(chunkInfo.chunkNumber));
  if (params.uploadId) formData.append("uploadId", params.uploadId);
  if (params.fileKey) formData.append("fileKey", params.fileKey);
  return formData;
};
